import { ArrowClockwiseIcon, FileTextIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import type { MouseEvent } from 'react';

import { CloseButton } from '@/components/common/close-button';

import styles from './upload.module.scss';

export type UploadEntry = {
  uid: string;
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  percent: number;
  error?: string;
  url?: string;
  preview?: string;
  retriable?: boolean;
};

function formatSize(bytes: number) {
  return `${(bytes / 1024 / 1024).toFixed(1)}Mb`;
}

/**
 * The image grid now renders *inside* antd's `<Dragger>` (Figma 430:405299 draws the thumbnails,
 * the "Upload more" pill and the counter inside the dashed container), and the dragger opens the
 * file picker on any click within it. Every control in an item therefore has to keep its click to
 * itself, or removing a thumbnail would also pop the picker.
 */
function contain(handler: () => void) {
  return (event: MouseEvent) => {
    event.stopPropagation();
    handler();
  };
}

type Props = {
  variant: 'image' | 'document';
  entry: UploadEntry;
  onRemove: (uid: string) => void;
  onRetry: (uid: string) => void;
};

export function UploadItem({ variant, entry, onRemove, onRetry }: Props) {
  if (variant === 'image') {
    return (
      <div className={styles.tile}>
        {entry.status === 'uploading' ? (
          <div className={styles.tileLoading}>{entry.percent}%</div>
        ) : entry.status === 'error' && entry.retriable ? (
          <button
            type="button"
            className={styles.tileLoading}
            onClick={contain(() => onRetry(entry.uid))}
          >
            <ArrowClockwiseIcon size={16} aria-hidden /> Retry
          </button>
        ) : entry.status === 'error' ? (
          <div className={styles.tileLoading}>{entry.error ?? 'Error'}</div>
        ) : (
          <img src={entry.preview ?? entry.url} alt={entry.name} />
        )}
        <div className={styles.tileRemove}>
          <CloseButton
            aria-label={`Remove ${entry.name}`}
            onClick={contain(() => onRemove(entry.uid))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <FileTextIcon size={24} aria-hidden className={styles.rowIcon} />
      <div className={styles.rowBody}>
        <div className={styles.rowName}>{entry.name}</div>
        {entry.status === 'error' ? (
          <div className={styles.rowError}>{entry.error ?? 'Upload failed'}</div>
        ) : (
          <div className={styles.rowMeta}>{formatSize(entry.size)}</div>
        )}
        {entry.status === 'uploading' ? (
          <div className={styles.bar}>
            <span style={{ width: `${entry.percent}%` }} />
          </div>
        ) : null}
      </div>
      {entry.status === 'error' && entry.retriable ? (
        <button type="button" className={styles.retry} onClick={contain(() => onRetry(entry.uid))}>
          <ArrowClockwiseIcon size={16} aria-hidden /> Retry
        </button>
      ) : null}
      <button
        type="button"
        className={styles.rowRemove}
        aria-label={`Remove ${entry.name}`}
        onClick={contain(() => onRemove(entry.uid))}
      >
        <TrashSimpleIcon size={16} aria-hidden />
      </button>
    </div>
  );
}
